import { AICreation, AICreationType } from '@/api/list-ai-creations';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import MediaGrid from '@/components/media-asset/MediaGrid';
import VideoCard from '@/components/videos/VideoCard';
import { useCurrentApp } from '@/stores/userDataStore';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { MediaAssetStatus, StandaloneMediaAsset } from '@/types/media';
import { TEMPLATES_SHOWING_MEDIA_ASSETS, useAllAiCreations, useAllAiCreationsCache } from '../hooks/useAllAiCreations';
import {
  MediaAssetPollingProvider,
  useMediaAssetPollingContext,
} from '@/features/ai-video-creator/components/MediaAssetPollingContext';
import { Card, EmptyState } from '@shopify/polaris';
import { StudioVideoStatus } from '@/features/ai-video-creator/stores/studioVideoStore';
import EmptyGrid from '@/components/EmptyGrid/EmptyGrid';
import { differenceInMinutes } from 'date-fns';
import { TemplateName } from '@/features/studio-video-templates/types/template';

interface AllCreationsListContentProps {
  selectedProduct?: { id: string; productId: string } | null;
}

const AllCreationsListContent = ({ selectedProduct }: AllCreationsListContentProps) => {
  const { appKey } = useCurrentApp();
  const { aiCreations, isLoading, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useAllAiCreations({
    appKey,
  });
  const { setPollingQueue } = useMediaAssetPollingContext();

  const pendingMediaAssets = useMemo(() => {
    return aiCreations.reduce<StandaloneMediaAsset[]>((acc, { item, type }) => {
      const isMediaAsset = type === AICreationType.mediaAsset;
      if (
        isMediaAsset &&
        item.status === MediaAssetStatus.PROCESSING &&
        differenceInMinutes(new Date(), new Date(item.updatedAt)) < 60
      ) {
        acc.push(item);
      }
      return acc;
    }, []);
  }, [aiCreations]);

  useEffect(() => {
    if (pendingMediaAssets.length > 0) {
      setPollingQueue(pendingMediaAssets);
    }

    return () => setPollingQueue([]);
  }, [pendingMediaAssets, setPollingQueue]);

  const renderMyAiCreationCard = useCallback((aiCreation: AICreation) => {
    if (aiCreation.type === AICreationType.mediaAsset) {
      return <MediaAssetCard key={aiCreation.item.id} media={aiCreation.item} inGrid />;
    }

    return <VideoCard key={aiCreation.item.id} video={aiCreation.item} options={{ preload: 'none' }} />;
  }, []);

  const creationsWithFilteredStudioVideos = useMemo(() => {
    const mediaAssetsWithSourceTemplate = aiCreations.filter((creation) => {
      if (creation.type !== AICreationType.mediaAsset) return false;

      const mediaAsset = creation.item as StandaloneMediaAsset;
      return (
        mediaAsset.metadata?.sourceTemplate &&
        TEMPLATES_SHOWING_MEDIA_ASSETS.includes(mediaAsset.metadata.sourceTemplate as TemplateName)
      );
    });

    const studioVideoIdsToHide = new Set(
      mediaAssetsWithSourceTemplate
        .map((creation) => {
          const mediaAsset = creation.item as StandaloneMediaAsset;
          return mediaAsset.metadata?.sourceStudioVideoId as string;
        })
        .filter(Boolean)
    );

    return aiCreations.filter((creation) => {
      if (creation.type !== AICreationType.studioVideo) {
        return true;
      }

      if (TEMPLATES_SHOWING_MEDIA_ASSETS.includes(creation?.item?.template as TemplateName)) {
        return !studioVideoIdsToHide.has(creation?.item?.id as string);
      }

      return true;
    });
  }, [aiCreations]);

  const filteredCreations = useMemo(() => {
    return creationsWithFilteredStudioVideos.filter((creation) => {
      if (!selectedProduct) {
        return true;
      }

      if (creation.type === AICreationType.mediaAsset && creation.item.productId) {
        return creation.item.productId === selectedProduct.productId;
      }

      if (creation.type === AICreationType.studioVideo && creation.item.product) {
        return creation.item.product.productId === selectedProduct.productId;
      }

      return false;
    });
  }, [creationsWithFilteredStudioVideos, selectedProduct]);

  const endReached = useCallback(() => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetching, hasNextPage]);

  if (isLoading || !appKey) {
    return <EmptyGrid staticHeight="calc(100vh - 270px)" />;
  }

  if (!aiCreations?.length) {
    return (
      <Card>
        <EmptyState
          heading="No content available"
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        />
      </Card>
    );
  }

  if (selectedProduct && !filteredCreations.length) {
    return (
      <Card>
        <EmptyState
          heading={`No content available for this product`}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        />
      </Card>
    );
  }

  return (
    <MediaGrid
      items={filteredCreations}
      renderItem={renderMyAiCreationCard}
      staticHeight="calc(100vh - 270px)"
      endReached={endReached}
      isFetchingNextPage={isFetchingNextPage}
    />
  );
};

interface AllCreationsListProps {
  selectedProduct?: { id: string; productId: string; title?: string } | null;
}

const AllCreationsList = ({ selectedProduct }: AllCreationsListProps) => {
  const { appKey } = useCurrentApp();
  const { updateMediaAssetsCache } = useAllAiCreationsCache({ appKey });

  return (
    <MediaAssetPollingProvider onCompletion={updateMediaAssetsCache}>
      <AllCreationsListContent selectedProduct={selectedProduct} />
    </MediaAssetPollingProvider>
  );
};

export default memo(AllCreationsList);
